import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import SocketContext from '../contexts/SocketContext';
import '../styles/VersionChecker.css';

const POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes
const GRACE_PERIOD = 10 * 60 * 1000; // 10 minutes

const VersionChecker = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [forceBlock, setForceBlock] = useState(false);
  const [remainingTime, setRemainingTime] = useState(null);
  const [newVersion, setNewVersion] = useState('');
  const currentHashRef = useRef(null);
  const graceTimerRef = useRef(null);
  const countdownRef = useRef(null);
  const graceEndRef = useRef(null);

  // Use useContext directly — returns null if provider hasn't set a value yet (user not authenticated)
  const socketContext = useContext(SocketContext);
  const socket = socketContext?.socket || null;

  const fetchVersion = useCallback(async () => {
    try {
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store',
      });
      if (!response.ok) return;
      const data = await response.json();

      if (!currentHashRef.current) {
        // First load — store as baseline
        currentHashRef.current = data.buildHash;
        return;
      }

      if (data.buildHash !== currentHashRef.current) {
        setNewVersion(data.version);
        triggerUpdate(false);
      }
    } catch {
      // Network error — silently ignore, will retry on next poll
    }
  }, []);

  const triggerUpdate = useCallback((force) => {
    if (force) {
      setForceBlock(true);
      setUpdateAvailable(true);
      return;
    }

    // Already in update state — don't restart grace period
    if (graceEndRef.current) return;

    setUpdateAvailable(true);
    graceEndRef.current = Date.now() + GRACE_PERIOD;

    // Start countdown display
    countdownRef.current = setInterval(() => {
      const remaining = Math.max(0, graceEndRef.current - Date.now());
      setRemainingTime(remaining);
      if (remaining <= 0) {
        clearInterval(countdownRef.current);
      }
    }, 1000);

    // Escalate to blocking overlay after grace period
    graceTimerRef.current = setTimeout(() => {
      setForceBlock(true);
    }, GRACE_PERIOD);
  }, []);

  // Poll for version changes
  useEffect(() => {
    fetchVersion();
    const interval = setInterval(fetchVersion, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchVersion]);

  // Listen for socket push
  useEffect(() => {
    if (!socket) return;

    const handleVersionUpdate = (data) => {
      setNewVersion(data?.version || '');
      triggerUpdate(data?.force === true);
    };

    socket.on('app_version_update', handleVersionUpdate);
    return () => {
      socket.off('app_version_update', handleVersionUpdate);
    };
  }, [socket, triggerUpdate]);

  // Cleanup timers
  useEffect(() => {
    return () => {
      if (graceTimerRef.current) clearTimeout(graceTimerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  const handleRefresh = () => {
    window.location.reload();
  };

  const formatTime = (ms) => {
    const totalSeconds = Math.ceil(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Phase 2: Blocking overlay
  if (forceBlock) {
    return (
      <div className="version-overlay">
        <div className="version-overlay-card">
          <div className="version-overlay-icon">&#x21bb;</div>
          <h2 className="version-overlay-title">Update Required</h2>
          <p className="version-overlay-message">
            Your session must be refreshed to continue using the system.
          </p>
          {newVersion && (
            <p className="version-overlay-version">New version: {newVersion}</p>
          )}
          <button className="version-overlay-button" onClick={handleRefresh}>
            Refresh Now
          </button>
        </div>
      </div>
    );
  }

  // Phase 1: Warning banner
  if (updateAvailable) {
    return (
      <div className="version-banner">
        <div className="version-banner-content">
          <span className="version-banner-icon">&#x26A0;</span>
          <span className="version-banner-text">
            A new update is available — please save your work and click Refresh.
            {remainingTime !== null && remainingTime > 0 && (
              <span className="version-banner-countdown">
                {' '}Update will be enforced in {formatTime(remainingTime)}
              </span>
            )}
          </span>
          <button className="version-banner-button" onClick={handleRefresh}>
            Refresh Now
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default VersionChecker;
